import React, { useEffect } from 'react';
import { useBranding } from '../../hooks/useBranding';
import { useForm } from 'react-hook-form';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { signUp } from '../../store/actions/user.actions';
import { clearError } from '../../store/reducers/user.reducer';

interface SignUpFormProps {
  onSwitchToLogin: () => void;
}

interface SignUpFormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
}

const authFieldSx = {
  '& .MuiInputBase-root': {
    backgroundColor: 'var(--auth-field-background) !important',
  },
  '& .MuiInputBase-root:hover': {
    backgroundColor: 'var(--auth-field-background) !important',
  },
  '& .MuiInputBase-root.Mui-focused': {
    backgroundColor: 'var(--auth-field-background) !important',
  },
  '& .MuiInputBase-input': {
    backgroundColor: 'transparent !important',
  },
  '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active': {
    WebkitBoxShadow: '0 0 0 1000px var(--auth-field-background) inset !important',
    boxShadow: '0 0 0 1000px var(--auth-field-background) inset !important',
    WebkitTextFillColor: 'var(--text-primary) !important',
    transition: 'background-color 5000s ease-in-out 0s',
  }
};

export const SignUpForm: React.FC<SignUpFormProps> = ({ onSwitchToLogin }) => {
  const dispatch = useAppDispatch();
  const { loading, error } = useAppSelector((state) => state.user);
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const branding = useBranding();
  const [pendingApproval, setPendingApproval] = React.useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<SignUpFormData>();

  const password = watch('password');

  useEffect(() => {
    // Clear any previous errors when component mounts
    dispatch(clearError());
  }, [dispatch]);

  const onSubmit = async (data: SignUpFormData) => {
    try {
      const { confirmPassword, firstName, lastName, ...signUpData } = data;
      const displayName = `${firstName.trim()} ${lastName.trim()}`.trim();
      const result = await dispatch(signUp({
        ...signUpData,
        displayName,
        firstName: firstName.trim(),
        lastName: lastName.trim()
      })) as any;
      if (result?.pendingApproval) {
        setPendingApproval(true);
      }
      reset();
    } catch (err) {
      console.error('Sign up error:', err);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  if (pendingApproval) {
    return (
      <div className="glassContainer auth-form-container" style={{ padding: '32px', maxWidth: '500px', background: 'radial-gradient(circle at center, rgba(181, 231, 255, 0.2), rgba(0, 0, 0, 0.63) 70%)', position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
        <div className="logobox glassBtn" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '24px' }}>
          <img src={branding.logoUrl || '/logo192.png'} alt="Logo" className="logo" style={{ height: 40, marginRight: 8 }} />
          <div style={{ fontSize: '24px', fontWeight: 'bold', fontFamily: 'inherit' }}>
            <span style={{ color: '#3a9397' }}>{branding.part1}</span>
            <span style={{ color: '#ff9800' }}>{branding.part2}</span>
          </div>
        </div>
        <Alert severity="success" sx={{ mb: 3, textAlign: 'left' }}>
          <strong>Registration submitted!</strong>
        </Alert>
        <Typography variant="body1" gutterBottom>
          Your account is pending admin approval. You will receive an email once your account has been reviewed.
        </Typography>
        <Button variant="text" onClick={onSwitchToLogin} sx={{ mt: 2, textTransform: 'none' }}>
          Back to Sign In
        </Button>
      </div>
    );
  }

  return (
    <div className="glassContainer auth-form-container" style={{ padding: '20px', maxWidth: '500px', maxHeight: '90vh', background:'radial-gradient(circle at center, rgba(181, 231, 255, 0.2), rgba(0, 0, 0, 0.63) 70%)', position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', overflowY: 'auto' }}>
      <div className="logobox glassBtn" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
        <img src={branding.logoUrl || '/logo192.png'} alt="Logo" className="logo" style={{ height: 40, marginRight: 8 }} />
        <div style={{ fontSize: '24px', fontWeight: 'bold', fontFamily: 'inherit' }}>
          <span style={{ color: '#3a9397' }}>{branding.part1}</span>
          <span style={{ color: '#ff9800' }}>{branding.part2}</span>
        </div>
      </div>
      <Typography variant="subtitle1" gutterBottom align="center" color="textSecondary">
        Join your family network
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            fullWidth
            label="First Name"
            {...register('firstName', {
              required: 'First name is required',
              minLength: {
                value: 2,
                message: 'First name must be at least 2 characters',
              },
            })}
            error={!!errors.firstName}
            helperText={errors.firstName?.message}
            disabled={loading}
            sx={authFieldSx}
          />
          <TextField
            fullWidth
            label="Last Name"
            {...register('lastName', {
              required: 'Last name is required',
              minLength: {
                value: 2,
                message: 'Last name must be at least 2 characters',
              },
            })}
            error={!!errors.lastName}
            helperText={errors.lastName?.message}
            disabled={loading}
            sx={authFieldSx}
          />
        </Box>

        <TextField
          fullWidth
          label="Email"
          type="email"
          {...register('email', {
            required: 'Email is required',
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: 'Invalid email address',
            },
          })}
          error={!!errors.email}
          helperText={errors.email?.message}
          sx={{ ...authFieldSx, mb: 2 }}
          disabled={loading}
        />

        <TextField
          fullWidth
          label="Password"
          type={showPassword ? 'text' : 'password'}
          {...register('password', {
            required: 'Password is required',
            minLength: {
              value: 6,
              message: 'Password must be at least 6 characters',
            },
          })}
          error={!!errors.password}
          helperText={errors.password?.message}
          sx={{ ...authFieldSx, mb: 2 }}
          disabled={loading}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleTogglePasswordVisibility}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          fullWidth
          label="Confirm Password"
          type={showConfirmPassword ? 'text' : 'password'}
          {...register('confirmPassword', {
            required: 'Please confirm your password',
            validate: (value) => value === password || 'Passwords do not match',
          })}
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword?.message}
          sx={{ mb: 3 }}
          disabled={loading}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle confirm password visibility"
                  onClick={handleToggleConfirmPasswordVisibility}
                  edge="end"
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading}
          sx={{ mb: 2 }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign Up'}
        </Button>
      </Box>

      <Typography align="center" variant="body2">
        Already have an account?{' '}
        <Button
          className='glassBtn'
          variant="text"
          onClick={onSwitchToLogin}
          disabled={loading}
          sx={{ textTransform: 'none' }}
        >
          SIGN IN
        </Button>
      </Typography>
    </div>
  );
};